// Legacy component : this is only used by the old edit portfolio modal

// Third Party
import React from 'react';
import classNames from 'classnames';

// Actions
import { LOAD_COMPARISON, SET_IS_RERUNNING } from '@/redux/modules/comparison';
import { resetReadStartTime } from '@/redux/modules/ai-statement-reader';
import { selectIsLoadingSome } from '@/redux/modules/ui/loaders';

// Selectors
import { connect } from 'react-redux';
import { selectShelfStatusedPortfolioHoldings } from '../../../redux/modules/shelf-management/selectors';

// Components
import PortfolioEditorBuilderModal from '@/views/components/modals/portfolio-editor-builder/PortfolioEditorBuilderModal';
import PortfolioReadOnlyModal from '@/views/components/modals/portfolio-modals/PortfolioReadOnlyModal';
import { Button } from '@/views/components/buttons';
import { LoadingSpinner } from '@/views/components/common';
import { EditPortfolio, EditPortfolioQuickAddContainer } from '../edit-portfolio';
import Modal from '@/views/components/modals/AccessibleModal';

// Utils
import { canAccessAIStatementReader } from '@/utils/feature-access';
import { getPortfolioMissingOrInvalidHoldings } from '../../../utils/portfolio/utils';
import { FormattedMessage } from '../../../utils/i18n';
import ThemeMode from '@/types/theme-mode';
import { userHasPermission, PERMISSIONS } from '../../../utils/permissions';
import Timestamp from '@/types/timestamp';
import { logSaveAIStatementPortfolio } from '@/services/usage-log';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { PortfolioBuilderContentOption, PortfolioCalculationPromiseReturn } from '../../../redux/modules/types/edit-portfolio';
import { setPortfolioContentOption } from '../../../redux/modules/edit-portfolio';
import Portfolio from '@/types/portfolio';
import PortfolioHolding from '@/types/portfolio-holding';
import Nullable from '@/types/nullable';
import CancellablePromise from '@/types/cancellable-promise';
import User from '@/types/user';

type OwnProps = {
  show: boolean;
  showActionOnSave?: boolean;
  onCancel: (portfolio?: Portfolio) => void;
  onSave: (portfolio: Portfolio) => Promise<Portfolio>;
  onPin?: () => void;
  saveLabel?: string;
  saveNotification?: string;
  onExited?: () => void;
  onRerunCurrentComparison?: () => Promise<void>;
};

type DispatchProps = {
  resetReadStartTime: () => void;
  setPortfolioContentOption: (option: PortfolioBuilderContentOption) => void;
};

type StateProps = {
  firstMissingHolding: PortfolioHolding | undefined;
  missingHoldingsCount: number;
  sidebarContentOption: PortfolioBuilderContentOption;
  calculationPromise?: Nullable<CancellablePromise<PortfolioCalculationPromiseReturn>>;
  portfolio: Portfolio;
  isReadOnly?: boolean;
  isRerunning: boolean;
  user?: Nullable<User>;
  aiStatementReadEndTime?: Timestamp;
  aiStatementReadStartTime?: Timestamp;
};

type State = {
  holdingSelected?: PortfolioHolding;
  initializedPortfolio: boolean;
  initializedQuickAdd: boolean;
  isComparisonReran: boolean;
  isExpanded: boolean;
  showPortfolioEditorBuilderModal: boolean;
  showReadOnlyPortfolioModal: boolean;
};

type Props = OwnProps & StateProps & DispatchProps;

class EditPortfolioModal extends React.PureComponent<Props, State> {
  private initialState: State = {
    isComparisonReran: false,
    isExpanded: false,
    initializedPortfolio: false,
    initializedQuickAdd: false,
    showPortfolioEditorBuilderModal: false,
    showReadOnlyPortfolioModal: false,
  };

  readonly state: State = this.initialState;

  componentDidMount(): void {
    this.updateModalsVisibility();
  }

  componentDidUpdate(prevProps) {
    if (
      (this.state.holdingSelected === undefined && this.props.firstMissingHolding !== undefined) ||
      prevProps.firstMissingHolding !== this.props.firstMissingHolding
    ) {
      this.setState({ holdingSelected: this.props.firstMissingHolding });
    }

    this.updateModalsVisibility();
  }

  componentWillUnmount(): void {
    if (this.props.show) {
      this.onCancel();
    }
  }

  updateModalsVisibility = () => {
    this.setState({
      showPortfolioEditorBuilderModal: this.props.show && !this.props.isReadOnly,
      showReadOnlyPortfolioModal: this.props.show && (this.props.isReadOnly ?? false),
    });
  };

  onToggleExpand = () => {
    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded,
    }));
  };

  setSidebarContentOption = (option: PortfolioBuilderContentOption) => {
    this.props.setPortfolioContentOption(option);
  };

  displayRiskProfile = () => {
    this.setSidebarContentOption(PortfolioBuilderContentOption.RiskProfile);
  };

  onSelectHoldingMissing = (holding: PortfolioHolding) => {
    this.setState({ holdingSelected: holding });
  };

  onCancel = () => {
    const { aiStatementReadStartTime, resetReadStartTime, user } = this.props;

    this.props.onCancel(this.props.portfolio);
    this.setState(this.initialState);

    if (canAccessAIStatementReader(user) && aiStatementReadStartTime) {
      resetReadStartTime();
    }
  };

  handleCloseOnLoading = () => {
    // [CONN-1291]: On close the modal, cancel the pending promises
    this.props.calculationPromise?.cancel();
    this.onCancel();
  };

  handleLoadedComponent = (property: keyof State) => {
    if (this.state[property]) return;
    this.setState({ ...this.state, [property]: true });
  };

  handleOnSave = async (portfolioParam: Portfolio) => {
    const { aiStatementReadEndTime, onSave, resetReadStartTime, user } = this.props;

    await onSave(portfolioParam);

    // TODO: CONN-8067 will have additional information added to this log.
    // Our current objective is to keep track of the amount of time spent reviewing with the AI statement reader.
    if (canAccessAIStatementReader(user) && aiStatementReadEndTime) {
      logSaveAIStatementPortfolio(aiStatementReadEndTime);
      resetReadStartTime();
    }

    this.setState({ isComparisonReran: false });
  };

  handleRerunCurrentComparison = () => {
    const { onRerunCurrentComparison } = this.props;

    if (!onRerunCurrentComparison) return;

    onRerunCurrentComparison().then(() => {
      this.setState({ isComparisonReran: true });
    });
  };

  renderPortfolioEditorBuilderModal = () => {
    const { user, showActionOnSave, onExited } = this.props;
    const { isComparisonReran, showPortfolioEditorBuilderModal, showReadOnlyPortfolioModal } = this.state;
    const showBuilderModal = showPortfolioEditorBuilderModal || isComparisonReran;

    if (!userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_BUILDER)) {
      return null;
    }

    return (
      <>
        <PortfolioEditorBuilderModal
          isComparisonReran={isComparisonReran}
          show={showBuilderModal}
          showActionOnSave={showActionOnSave}
          onCancel={this.handleCloseOnLoading}
          onExited={onExited}
          onSave={this.handleOnSave}
        />
        <PortfolioReadOnlyModal
          show={showReadOnlyPortfolioModal}
          onClose={() => this.onCancel()}
          onRerunCurrentComparison={this.handleRerunCurrentComparison}
        />
      </>
    );
  };

  renderLegacyPortfolioEditor = () => {
    // Note : when we render the new portfolio editor builder, we currently also render the legacy modal under it.
    // Brij said he did it because "there was an issue with the missing product flow in one of the existing cases.
    // Also I remember there was something with the Compare Alternatives as well.
    // It is coz of that re rendering thing that happens with the Builder is what I recall."
    // TODO : do not render the legacy modal when the new portfolio editor builder is displayed

    const { show, saveNotification, onPin, onExited, sidebarContentOption, isReadOnly, missingHoldingsCount, user } = this.props;
    const { isExpanded, initializedPortfolio, initializedQuickAdd } = this.state;
    const hasMissingHoldings = this.props.firstMissingHolding !== undefined;
    const holdingSelected = this.state.holdingSelected !== undefined ? this.state.holdingSelected : this.props.firstMissingHolding;

    // CONN-9263 : Since this legacy portfolio editor still overlaps portfolio editor builder modal,
    // we are unable to perform any call to action on portfolio modal.
    // This is a temporary solution until we remove the legacy portfolio editor.
    const showBuilderModal = show && !userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_BUILDER);

    return (
      <Modal
        show={show}
        className={classNames('edit-portfolio-modal', {
          'is-expanded': isExpanded,
          'is-read-only': isReadOnly,
          'is-hidden': !showBuilderModal,
          light: user?.preferences?.theme === ThemeMode.LIGHT,
        })}
        onHide={this.onCancel}
        dialogClassName="modal-dialog-centered"
        backdrop="static"
        keyboard={false}
        onExited={() => {
          onExited && onExited();
          this.setState(this.initialState);
        }}
      >
        <Modal.Body className="d-flex">
          {(!initializedPortfolio || (!initializedQuickAdd && !isReadOnly)) && (
            <div className="edit-modal-loader d-flex align-items-center justify-content-center">
              <LoadingSpinner className="edit-modal-loader-spinner" />
              <Button onClick={this.handleCloseOnLoading} className="cancel-button edit-modal-loader-close">
                <FormattedMessage id="Common.Close" />
              </Button>
            </div>
          )}

          {!isExpanded && !userHasPermission(user, PERMISSIONS.PORTFOLIO_EDITOR_BUILDER) && (
            <EditPortfolio
              onSave={this.props.onSave}
              onCancel={this.onCancel}
              onPin={onPin}
              saveLabel={this.props.saveLabel}
              showQuickSearch={false}
              withCharts={false}
              saveNotification={saveNotification}
              displayRiskProfile={this.displayRiskProfile}
              sidebarContentOption={sidebarContentOption}
              setSidebarContentOption={this.setSidebarContentOption}
              onSelectHoldingMissing={this.onSelectHoldingMissing}
              holdingSelected={holdingSelected}
              hasMissingHoldings={hasMissingHoldings}
              componentIsReady={() => this.handleLoadedComponent('initializedPortfolio')}
            />
          )}
          {!isReadOnly && (
            <EditPortfolioQuickAddContainer
              onToggleExpand={this.onToggleExpand}
              onPin={onPin}
              isExpanded={isExpanded}
              sidebarContentOption={sidebarContentOption}
              setSidebarContentOption={this.setSidebarContentOption}
              holdingSelected={holdingSelected}
              missingHoldingIsSelected={holdingSelected !== undefined}
              missingHoldingsCount={missingHoldingsCount}
              componentIsReady={() => this.handleLoadedComponent('initializedQuickAdd')}
            />
          )}
        </Modal.Body>
      </Modal>
    );
  };

  render() {
    return (
      <>
        {this.renderPortfolioEditorBuilderModal()}
        {this.renderLegacyPortfolioEditor()}
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const missingHoldings = getPortfolioMissingOrInvalidHoldings(selectShelfStatusedPortfolioHoldings(state.editPortfolio.portfolio));

  return {
    firstMissingHolding: missingHoldings.find(h => h !== undefined),
    missingHoldingsCount: missingHoldings.length,
    sidebarContentOption: state.editPortfolio.contentOption,
    calculationPromise: state.editPortfolio.calculationPromise,
    portfolio: state.editPortfolio.portfolio,
    isReadOnly: state.comparison.portfolioEditor.isReadOnly,
    isRerunning: selectIsLoadingSome(state, [SET_IS_RERUNNING.type, LOAD_COMPARISON.type]),
    user: state.auth.user,
    aiStatementReadEndTime: state.aiStatementReader.current.activityLogs?.aiStatementReadEndTime,
    aiStatementReadStartTime: state.aiStatementReader.current.activityLogs?.aiStatementReadStartTime,
  };
};

export default connect(mapStateToProps, {
  resetReadStartTime,
  setPortfolioContentOption,
})(EditPortfolioModal);
